import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import { minidenticon } from "minidenticons";
import { type ComponentProps, useMemo } from "react";

import RenderIf from "common/components/RenderIf";
import LoadableImage from "common/components/ui/LoadableImage";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";

import AgencyIcon from "assets/icons/plans/agency-gradient-outline.svg?react";

const saturation = 75;
const lightness = 50;

const workspaceImageVariants = cva("", {
  variants: {
    size: {
      xs: "size-7 rounded-xl",
      sm: "size-8 rounded-xl",
      md: "size-10 rounded-2xl",
      lg: "size-14 rounded-4xl",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export type WorkspaceImageProps = Omit<
  ComponentProps<typeof LoadableImage>,
  "src"
> &
  VariantProps<typeof workspaceImageVariants> & {
    id: string;
    imageSrc: string;
  };

export default function WorkspaceImage({
  id,
  imageSrc,
  size,
  className,
  ...props
}: WorkspaceImageProps) {
  const { agencyLicensedAccounts } = useLicensedAccounts(id);

  const svgURI = useMemo(
    () =>
      `data:image/svg+xml;utf8,${encodeURIComponent(
        minidenticon(id, saturation, lightness),
      )}`,
    [id],
  );

  return (
    <div className={clsx("relative shrink-0", className)}>
      <LoadableImage
        {...props}
        className={workspaceImageVariants({
          size,
          className: !imageSrc && "border border-black-200",
        })}
        src={imageSrc || svgURI}
      />
      <RenderIf condition={!!agencyLicensedAccounts.length}>
        <AgencyIcon className="absolute -bottom-1 -right-1 size-4" />
      </RenderIf>
    </div>
  );
}
